// HtmlContent's sanitizer (striptags) only filters HTML tag names — it has no visibility into
// CSS syntax, so raw CSS dropped into a <style> tag sails through untouched. `@import` loads a
// whole remote stylesheet, and `url(...)` (background/image/font loads, or the attribute/sibling
// selector exfiltration technique) lets custom CSS reach out to an attacker-controlled origin
// from inside a customer-facing presentation. `expression(...)` is a legacy IE CSS/JS execution
// vector, stripped for the same reason.
//
// This is a best-effort regex pass, not a real CSS parser — if the Custom block ever needs more
// permissive CSS (e.g. local background-images), reach for an actual CSS sanitizer rather than
// extending these patterns.
export function sanitizeCustomCss(css: string): string {
  if (!css) return ''

  const withoutImports = css.replace(/@import[^;]*;?/gi, '')
  const withoutUrls = replaceBalancedCall(withoutImports, 'url', 'none')
  return replaceBalancedCall(withoutUrls, 'expression', 'none')
}

// `[^)]*` inside a regex breaks on nested parens (e.g. `expression(alert(1))` — matches only up
// to the first `)`, leaving `alert(1))` behind). Scan for the matching close paren instead.
function replaceBalancedCall(css: string, fnName: string, replacement: string): string {
  // fnName is always one of this file's own hardcoded literals ('url'/'expression'), never
  // user input, so the dynamic pattern here can't be attacker-influenced.
  // eslint-disable-next-line security/detect-non-literal-regexp
  const callStart = new RegExp(`${fnName}\\s*\\(`, 'gi')
  let result = ''
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = callStart.exec(css))) {
    const openParenIndex = match.index + match[0].length - 1
    let depth = 1
    let idx = openParenIndex + 1
    while (idx < css.length && depth > 0) {
      if (css[idx] === '(') depth++
      else if (css[idx] === ')') depth--
      idx++
    }

    result += css.slice(lastIndex, match.index) + replacement
    lastIndex = idx
    callStart.lastIndex = idx
  }

  return result + css.slice(lastIndex)
}
